import { useEffect, useMemo, useState } from "react";
import {
    Modal, ModalDialog, Stack, Typography, Select, Option, Button,
    Textarea, Input, Chip, Box, Divider, CircularProgress
} from "@mui/joy";

const STAGE_LABELS = {
    pending_assignment: 'На согласовании',
    measuring_assigned: 'Назначен обмер',
    closed_by_surveyor: 'Закрыт замерщиком',
    in_drawing: 'Взят в черчение',
    quality_check: 'Контроль качества',
    ready_for_delivery: 'Готово к выдаче',
    completed: 'Завершён',
    cancelled: 'Отменён',
};

const Q_STATUSES = [
    { value: 'yes',               label: 'Выезд состоялся' },
    { value: 'moved',             label: 'Перенесён' },
    { value: 'cancelled',         label: 'Отменён' },
    { value: 'postponed_to_date', label: 'Перенесён на конкретную дату' },
];

// requiredFields — массив от сервера: [{field,type,label,required,role?}]
export default function ModalKanbanTransition({
    open, event, fromStage, toStage, requiredFields = [], employees = [],
    onConfirm, onCancel,
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState('');

    // Поля
    const [surveyorId, setSurveyorId]   = useState(null);
    const [draftsmanId, setDraftsmanId] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [square, setSquare] = useState('');

    // Анкета
    const [qStatus, setQStatus] = useState('yes');
    const [qComment, setQComment] = useState('');
    const [qLink, setQLink] = useState('');
    const [qTransferDate, setQTransferDate] = useState('');
    const [qTransferReason, setQTransferReason] = useState('');
    const [qCancelReason, setQCancelReason] = useState('');

    // Резолюция по выходу из «Работа не принята» (Задача 1)
    const [reworkResolution, setReworkResolution] = useState('');

    const needSurveyor  = requiredFields.some(f => f.field === 'employee_id');
    const needDraftsman = requiredFields.some(f => f.field === 'draftsman_id');
    const needQuest     = requiredFields.some(f => f.field === 'questionnaire');
    const needCancel    = requiredFields.some(f => f.field === 'cancel_reason');
    const reworkField   = requiredFields.find(f => f.field === 'rework_resolution');
    const needRework    = !!reworkField;

    const surveyors = useMemo(
        () => (employees || []).filter(e => Array.isArray(e.position) && e.position.includes('surveyor')),
        [employees]
    );
    const draftsmen = useMemo(
        () => (employees || []).filter(e => Array.isArray(e.position) && e.position.includes('draftsman')),
        [employees]
    );

    useEffect(() => {
        if (!open) return;
        setError(''); setLoading(false);
        setSurveyorId(null); setDraftsmanId(null); setCancelReason(''); setSquare('');
        setReworkResolution('');
        const q = event?.questionnaire;
        setQStatus(q?.status || 'yes');
        setQComment(q?.comment || '');
        setQLink(q?.link_media_file || '');
        setQTransferDate(q?.transfer_date_replacement?.slice(0, 16) || '');
        setQTransferReason(q?.transfer_reason || '');
        setQCancelReason(q?.reason_for_cancelation || '');
    }, [open, event]);

    const buildPayload = () => {
        const payload = {};
        if (needSurveyor)  payload.employee_id  = surveyorId;
        if (needDraftsman) payload.draftsman_id = draftsmanId;
        if (needCancel)    payload.cancel_reason = cancelReason.trim();
        if (needRework)    payload.rework_resolution = reworkResolution.trim();
        if (square !== '') payload.square = square;
        if (needQuest) {
            payload.questionnaire = {
                status: qStatus,
                comment: qComment || null,
                link_media_file: qLink || null,
                transfer_date_replacement: qStatus === 'postponed_to_date' ? (qTransferDate || null) : null,
                transfer_reason: qStatus === 'moved' ? (qTransferReason || null) : null,
                reason_for_cancelation: qStatus === 'cancelled' ? (qCancelReason || null) : null,
            };
        }
        return payload;
    };

    const validate = () => {
        if (needSurveyor && !surveyorId)   return 'Выберите обмерщика';
        if (needDraftsman && !draftsmanId) return 'Выберите чертёжника';
        if (needCancel && !cancelReason.trim()) return 'Укажите причину отмены';
        if (needRework && !reworkResolution.trim())
            return reworkField?.outcome === 'positive'
                ? 'Опишите, что было исправлено'
                : 'Укажите резолюцию';
        return '';
    };

    const handleConfirm = async () => {
        const v = validate();
        if (v) { setError(v); return; }
        setLoading(true); setError('');
        const res = await onConfirm(buildPayload());
        setLoading(false);
        if (!res?.ok) setError(res?.message || 'Не удалось сменить стадию');
    };

    return (
        <Modal
            open={open}
            // нельзя закрыть по фону/escape — только через «Отмена»
            onClose={() => {}}
            disableEscapeKeyDown
            sx={{ zIndex: 10105 }}
        >
            <ModalDialog sx={{ maxWidth: 520, width: '100%', borderRadius: '14px' }}>
                <Typography level="title-md" sx={{ fontWeight: 800, mb: 1 }}>
                    Смена стадии события
                </Typography>

                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip variant="soft" color="neutral" sx={{ borderRadius: '8px' }}>
                            {STAGE_LABELS[fromStage] || fromStage}
                        </Chip>
                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>→</Typography>
                        <Chip variant="soft" color="primary" sx={{ borderRadius: '8px' }}>
                            {STAGE_LABELS[toStage] || toStage}
                        </Chip>
                    </Stack>

                    {needSurveyor && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Обмерщик</Typography>
                            <Select size="sm" value={surveyorId} placeholder="Выберите обмерщика"
                                    onChange={(_, v) => setSurveyorId(v)} sx={{ borderRadius: '10px' }}
                                    slotProps={{ listbox: { sx: { zIndex: 10106 } } }}>
                                {surveyors.map(e => (
                                    <Option key={e.id} value={e.id}>
                                        {e.last_name || ''} {e.name} {e.city_bitrix?.name ? `· ${e.city_bitrix.name}` : ''}
                                    </Option>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {needDraftsman && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Чертёжник</Typography>
                            <Select size="sm" value={draftsmanId} placeholder="Выберите чертёжника"
                                    onChange={(_, v) => setDraftsmanId(v)} sx={{ borderRadius: '10px' }}
                                    slotProps={{ listbox: { sx: { zIndex: 10106 } } }}>
                                {draftsmen.map(e => (
                                    <Option key={e.id} value={e.id}>
                                        {e.last_name || ''} {e.name}
                                    </Option>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {needQuest && (
                        <>
                            <Divider />
                            <Typography level="body-sm" sx={{ fontWeight: 700 }}>Анкета обмерщика</Typography>
                            <Box>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Статус выезда</Typography>
                                <Select size="sm" value={qStatus} onChange={(_, v) => setQStatus(v)}
                                        sx={{ borderRadius: '10px' }}
                                        slotProps={{ listbox: { sx: { zIndex: 10106 } } }}>
                                    {Q_STATUSES.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Box>
                            <Box>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Комментарий</Typography>
                                <Textarea minRows={2} value={qComment}
                                          onChange={(e) => setQComment(e.target.value)} sx={{ borderRadius: '10px' }} />
                            </Box>
                            <Box>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Ссылка на материалы (Я.Диск/Bitrix.Диск)</Typography>
                                <Input size="sm" value={qLink} onChange={(e) => setQLink(e.target.value)}
                                       sx={{ borderRadius: '10px' }} />
                            </Box>
                            {qStatus === 'postponed_to_date' && (
                                <Input size="sm" type="datetime-local" value={qTransferDate}
                                       onChange={(e) => setQTransferDate(e.target.value)} sx={{ borderRadius: '10px' }} />
                            )}
                            {qStatus === 'moved' && (
                                <Textarea minRows={2} placeholder="Причина переноса" value={qTransferReason}
                                          onChange={(e) => setQTransferReason(e.target.value)} sx={{ borderRadius: '10px' }} />
                            )}
                            {qStatus === 'cancelled' && (
                                <Textarea minRows={2} placeholder="Причина отмены" value={qCancelReason}
                                          onChange={(e) => setQCancelReason(e.target.value)} sx={{ borderRadius: '10px' }} />
                            )}
                        </>
                    )}

                    {needCancel && (
                        <Box>
                            <Typography level="body-xs" sx={{ mb: 0.5 }}>Причина отмены</Typography>
                            <Textarea minRows={2} value={cancelReason}
                                      onChange={(e) => setCancelReason(e.target.value)} sx={{ borderRadius: '10px' }} />
                        </Box>
                    )}

                    {needRework && (
                        <Box>
                            <Divider sx={{ mb: 1 }} />
                            <Typography level="body-sm" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {reworkField?.outcome === 'positive'
                                    ? 'Работа принята'
                                    : 'Закрытие работы'}
                            </Typography>
                            <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                                {reworkField?.label || 'Опишите резолюцию по доработке'}
                            </Typography>
                            <Textarea minRows={2} value={reworkResolution}
                                      placeholder={reworkField?.outcome === 'positive'
                                          ? 'Что было исправлено'
                                          : 'Почему закрываем'}
                                      onChange={(e) => setReworkResolution(e.target.value)}
                                      sx={{ borderRadius: '10px' }} />
                        </Box>
                    )}

                    {error && <Typography level="body-sm" color="danger">{error}</Typography>}

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="soft" color="neutral" onClick={onCancel} disabled={loading}
                                sx={{ borderRadius: '10px', flex: 1 }}>
                            Отмена
                        </Button>
                        <Button variant="solid" color="primary" onClick={handleConfirm} loading={loading}
                                
                                sx={{ borderRadius: '10px', flex: 2, fontWeight: 700 }}>
                            Подтвердить
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}